import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Avatar,
  Stack,
  Button,
  ButtonGroup,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HistoryIcon from '@mui/icons-material/History';
import SpeedIcon from '@mui/icons-material/Speed';
import StarIcon from '@mui/icons-material/Star';
import PeopleIcon from '@mui/icons-material/People';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DownloadIcon from '@mui/icons-material/Download';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';

import * as XLSX from 'xlsx';

const apiUrl = process.env.REACT_APP_API_URL;

function WorkshopSessionsList({
  sessions,
  viewMode,
  onOpenParticipants,
  analyticsData,
}) {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const cleanDate =
      typeof dateString === 'string'
        ? dateString.replace(' ', 'T')
        : dateString;
    const d = new Date(cleanDate);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // 1. État pour gérer l'affichage de la suite du classement
  const [showAllClients, setShowAllClients] = useState(false);

  // 2. Tri décroissant par taux de participation (%)
  const sortedClients = useMemo(() => {
    if (viewMode !== 'client') return [];
    const allClients = analyticsData?.meilleurs_clients?.all_clients || [];

    return [...allClients].sort((a, b) => {
      const rateA = a.participation_rate ?? 0;
      const rateB = b.participation_rate ?? 0;
      return rateB - rateA;
    });
  }, [analyticsData, viewMode]);

  // 3. Découpage : On prend les 3 premiers, ou la totalité si "showAllClients" est vrai
  const visibleClients = useMemo(() => {
    if (showAllClients) return sortedClients;
    return sortedClients.slice(0, 3); // Uniquement le top 3 au départ
  }, [sortedClients, showAllClients]);

  // --- RENDU SPÉCIFIQUE : ONGLET PARTICIPANTS ---
  if (viewMode === 'client') {
    if (!sortedClients || sortedClients.length === 0) {
      return (
        <Typography
          variant="caption"
          sx={{ color: '#94a3b8', fontStyle: 'italic', pl: 1 }}
        >
          Aucune donnée client sur cet atelier
        </Typography>
      );
    }

    return (
      <Stack spacing={1.5} sx={{ width: '100%' }}>
        {visibleClients.map((client, index) => {
          const displayName =
            `${client.prenom ?? ''} ${client.nom ?? ''}`.trim() ||
            'Client Anonyme';
          const email = client.email_client || "Pas d'adresse email";
          const phone = client.phone || 'Pas de numéro de téléphone';
          const count = client.participations_count ?? 0;
          const rate = client.participation_rate ?? 0;
          const totalSessions = client.total_sessions_workshop ?? 0;

          // 🎨 COULEURS PAR CLASSEMENT
          let cardBg = '#f8fafc';
          let cardBorder = '#e2e8f0';
          let avatarBg = '#64748b';
          let badgeLabel = null;

          if (index === 0) {
            cardBg = '#fef9c3'; // 🥇 Or
            cardBorder = '#fef08a';
            avatarBg = '#ca8a04';
            badgeLabel = '1er';
          } else if (index === 1) {
            cardBg = '#f1f5f9'; // 🥈 Argent
            cardBorder = '#cbd5e1';
            avatarBg = '#475569';
            badgeLabel = '2ème';
          } else if (index === 2) {
            cardBg = '#ffedd5'; // 🥉 Bronze
            cardBorder = '#fed7aa';
            avatarBg = '#ea580c';
            badgeLabel = '3ème';
          }

          return (
            <Box
              key={client.client_id}
              sx={{
                p: 1.5,
                borderRadius: '8px',
                bgcolor: cardBg,
                border: `1px solid ${cardBorder}`,
                width: '100%',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-1px)' },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: avatarBg,
                    color: '#ffffff',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    mt: 0.5,
                  }}
                >
                  {badgeLabel ? badgeLabel[0] : index + 1}
                </Avatar>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        fontWeight: 700,
                        color: index === 0 ? '#854d0e' : '#1e3a8a',
                        fontSize: '0.8rem',
                        textTransform: 'capitalize',
                      }}
                    >
                      {displayName}
                    </Typography>

                    {badgeLabel && (
                      <Chip
                        label={badgeLabel}
                        size="small"
                        sx={{
                          height: '16px',
                          fontSize: '0.55rem',
                          fontWeight: 800,
                          bgcolor: '#ffffff',
                          border: '1px solid',
                          borderColor: cardBorder,
                          color: avatarBg,
                        }}
                      />
                    )}
                  </Stack>

                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: '#64748b',
                      fontSize: '0.68rem',
                      display: 'block',
                    }}
                  >
                    {email}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: '#64748b',
                      fontSize: '0.68rem',
                      display: 'block',
                    }}
                  >
                    {phone}
                  </Typography>
                </Box>

                <Stack
                  direction="column"
                  spacing={0.5}
                  alignItems="flex-end"
                  sx={{ minWidth: 'fit-content' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${count} résa.`}
                      size="small"
                      sx={{
                        bgcolor: '#3b82f6',
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: '20px',
                      }}
                    />
                    <Chip
                      label={`${rate}%`}
                      size="small"
                      sx={{
                        bgcolor:
                          rate > 75
                            ? '#dcfce7'
                            : rate > 40
                              ? '#dbeafe'
                              : '#f1f5f9',
                        color:
                          rate > 75
                            ? '#15803d'
                            : rate > 40
                              ? '#1d4ed8'
                              : '#475569',
                        border: `1px solid ${rate > 75 ? '#bbf7d0' : rate > 40 ? '#bfdbfe' : '#e2e8f0'}`,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: '20px',
                      }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      color: '#64748b',
                      textTransform: 'lowercase',
                      fontStyle: 'italic',
                    }}
                  >
                    Présence ({count}/{totalSessions} sess.)
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          );
        })}

        {/* 🔥 BOUTON DYNAMIQUE : Affiché uniquement s'il y a plus de 3 clients au total */}
        {sortedClients.length > 3 && (
          <Button
            size="small"
            variant="text"
            onClick={() => setShowAllClients(!showAllClients)}
            endIcon={
              showAllClients ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )
            }
            sx={{
              alignSelf: 'center',
              color: '#2563eb',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: '#f1f5f9' },
            }}
          >
            {showAllClients
              ? 'Voir moins'
              : `Voir plus (${sortedClients.length - 3} autres participants)`}
          </Button>
        )}
      </Stack>
    );
  }
  // --- RENDU SPÉCIFIQUE : ANALYTICS ---
  if (viewMode === 'analytics' && analyticsData) {
    const fillRate = analyticsData.global_fill_rate || 0;
    const topSessions = analyticsData.top_sessions || [];
    const worstSession = analyticsData.worst_session;

    return (
      <Stack spacing={1.5} sx={{ width: '100%' }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: '8px',
            bgcolor: '#f0fdfa',
            border: '1px solid #ccfbf1',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: '#0f766e' }}
            >
              Performance Globale
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: '#334155' }}
            >
              {analyticsData.workshop?.sessions_count || 0} sessions
            </Typography>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: '#0d9488', fontSize: '0.75rem' }}
            >
              {fillRate}% de remplissage moyen
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 500 }}
            >
              {analyticsData.global_participants || 0} /{' '}
              {analyticsData.global_capacity || 0} places
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(fillRate, 100)}
            sx={{
              height: 6,
              borderRadius: '3px',
              backgroundColor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                borderRadius: '3px',
                backgroundColor: '#0d9488',
              },
            }}
          />
        </Box>

        <Stack spacing={1} sx={{ pl: 0.5 }}>
          {topSessions.length > 0 && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ mb: 0.5 }}
              >
                <WorkspacePremiumIcon sx={{ fontSize: 14, color: '#eab308' }} />
                <Typography
                  variant="caption"
                  sx={{ color: '#475569', fontSize: '0.7rem', fontWeight: 700 }}
                >
                  Top Sessions ({topSessions.length}) :
                </Typography>
              </Stack>
              <Stack spacing={0.5} sx={{ pl: 1 }}>
                {topSessions.map((s, idx) => (
                  <Typography
                    key={s.session_id || idx}
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontSize: '0.68rem',
                      display: 'block',
                    }}
                  >
                    • Le <strong>{formatDate(s.date)}</strong> :{' '}
                    {s.participants}/{s.capacity} pl. (
                    <span style={{ color: '#16a34a', fontWeight: 600 }}>
                      {s.fill_rate}%
                    </span>
                    )
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {worstSession && (
            <Box sx={{ pt: 0.5 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ mb: 0.2 }}
              >
                <TrendingDownIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                <Typography
                  variant="caption"
                  sx={{ color: '#475569', fontSize: '0.7rem', fontWeight: 700 }}
                >
                  Moins performante :
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  fontSize: '0.68rem',
                  pl: 1,
                  display: 'block',
                }}
              >
                • Le <strong>{formatDate(worstSession.date)}</strong> :{' '}
                {worstSession.participants}/{worstSession.capacity} pl. (
                <span style={{ color: '#dc2626', fontWeight: 600 }}>
                  {worstSession.fill_rate}%
                </span>
                )
              </Typography>
            </Box>
          )}
        </Stack>
      </Stack>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Typography
        variant="caption"
        sx={{ color: '#94a3b8', fontStyle: 'italic', pl: 1 }}
      >
        {viewMode === 'active'
          ? 'Pas de session planifiée'
          : 'Aucun historique pour cet atelier'}
      </Typography>
    );
  }

  const displayedSessions = expanded ? sessions : sessions.slice(0, 3);
  const hasMore = sessions.length > 3;
  const remainingCount = sessions.length - 3;

  const formatLongDate = (dateString) => {
    if (!dateString) return '—';
    const cleanDate =
      typeof dateString === 'string'
        ? dateString.replace(' ', 'T')
        : dateString;
    const d = new Date(cleanDate);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (rate) => {
    if (rate === 0) return '#94a3b8';
    if (rate >= 100) return '#ef4444';
    if (rate >= 80) return '#10b981';
    return '#4f46e5';
  };

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      {displayedSessions.map((session, index) => {
        const currentFillRate =
          session.fill_rate !== undefined
            ? session.fill_rate
            : Math.round(
                ((session.participants_count || 0) / (session.capacity || 1)) *
                  100
              ) || 0;

        const sessionColor = getStatusColor(currentFillRate);
        const isUpcoming =
          session.status === 'active' || session.status === 'upcoming';
        const hasParticipants =
          session.participants && session.participants.length > 0;

        return (
          <Box
            key={session.session_id || index}
            sx={{
              p: 1.5,
              borderRadius: '8px',
              bgcolor: viewMode === 'active' ? '#f8fafc' : '#fcfcfd',
              border: `1px solid ${viewMode === 'active' ? '#e2e8f0' : '#f1f5f9'}`,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarTodayIcon sx={{ fontSize: 13, color: '#64748b' }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: '#334155' }}
                >
                  {formatLongDate(session.date)}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: isUpcoming ? '#166534' : '#475569',
                  bgcolor: isUpcoming ? '#f0fdf4' : '#e2e8f0',
                  px: 0.8,
                  py: 0.2,
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                }}
              >
                {isUpcoming ? 'À venir' : 'Terminée'}
              </Typography>
            </Stack>

            <Box sx={{ width: '100%' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: sessionColor,
                    fontSize: '0.75rem',
                  }}
                >
                  {currentFillRate}% rempli
                </Typography>

                <Chip
                  icon={<PeopleIcon sx={{ fontSize: '12px !important' }} />}
                  label={`${session.participants_count || 0} / ${session.capacity || 0} places`}
                  size="small"
                  onClick={(e) => onOpenParticipants(e, session.participants)}
                  disabled={!hasParticipants}
                  sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    cursor: hasParticipants ? 'pointer' : 'default',
                    bgcolor: hasParticipants ? '#eff6ff' : '#f1f5f9',
                    color: hasParticipants ? '#1d4ed8' : '#64748b',
                    border: `1px solid ${hasParticipants ? '#bfdbfe' : '#e2e8f0'}`,
                    '&:hover': {
                      bgcolor: hasParticipants ? '#dbeafe' : '#f1f5f9',
                    },
                  }}
                />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(currentFillRate, 100)}
                sx={{
                  height: 5,
                  borderRadius: '3px',
                  backgroundColor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: '3px',
                    backgroundColor: sessionColor,
                  },
                }}
              />
            </Box>
          </Box>
        );
      })}

      {hasMore && (
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded(!expanded)}
          endIcon={
            expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
          }
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: '#4f46e5',
            alignSelf: 'flex-start',
            py: 0,
            minWidth: 0,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          {expanded ? 'Voir moins' : `Voir plus (+${remainingCount})`}
        </Button>
      )}
    </Stack>
  );
}

WorkshopSessionsList.propTypes = {
  sessions: PropTypes.array,
  viewMode: PropTypes.string.isRequired,
  onOpenParticipants: PropTypes.func.isRequired,
  analyticsData: PropTypes.object,
};

export default function AteliersSessionsAnalyticsTable({ data }) {
  const [viewMode, setViewMode] = useState('active');
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeParticipants, setActiveParticipants] = useState([]);
  const [workshopsExpanded, setWorkshopsExpanded] = useState(false);

  // Filtrage et Tri adaptif
  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    let processedData = [...data];

    if (viewMode === 'performance') {
      return processedData
        .filter((w) => {
          const rate = w.analytics?.global_fill_rate || w.fill_rate || 0;
          return rate >= 5;
        })
        .sort((a, b) => {
          const rateA = a.analytics?.global_fill_rate || a.fill_rate || 0;
          const rateB = b.analytics?.global_fill_rate || b.fill_rate || 0;
          return rateB - rateA;
        });
    }

    if (viewMode === 'analytics' || viewMode === 'client') {
      return processedData.sort(
        (a, b) =>
          (b.analytics?.global_fill_rate || 0) -
          (a.analytics?.global_fill_rate || 0)
      );
    }

    return processedData.sort((a, b) => {
      const rateA = a.fill_rate || a.analytics?.global_fill_rate || 0;
      const rateB = b.fill_rate || b.analytics?.global_fill_rate || 0;
      return rateB - rateA;
    });
  }, [data, viewMode]);

  const displayedWorkshops = useMemo(() => {
    return workshopsExpanded ? sortedData : sortedData.slice(0, 3);
  }, [sortedData, workshopsExpanded]);

  const hasMoreWorkshops = sortedData.length > 3;
  const remainingWorkshopsCount = sortedData.length - 3;

  const handleOpenParticipants = (event, participantsList) => {
    if (!participantsList || participantsList.length === 0) return;
    setAnchorEl(event.currentTarget);
    setActiveParticipants(participantsList);
  };

  const handleCloseParticipants = () => {
    setAnchorEl(null);
    setActiveParticipants([]);
  };

  const renderRankBadge = (index) => {
    if (viewMode !== 'performance') return null;

    const rank = index + 1;
    let badgeBg = '#f1f5f9';
    let badgeColor = '#475569';
    let iconColor = null;

    if (rank === 1) {
      badgeBg = '#fef9c3';
      badgeColor = '#854d0e';
      iconColor = '#eab308';
    }
    if (rank === 2) {
      badgeBg = '#e2e8f0';
      badgeColor = '#334155';
      iconColor = '#94a3b8';
    }
    if (rank === 3) {
      badgeBg = '#ffedd5';
      badgeColor = '#9a3412';
      iconColor = '#ca8a04';
    }

    return (
      <Chip
        icon={
          iconColor ? (
            <EmojiEventsIcon style={{ color: iconColor, fontSize: '14px' }} />
          ) : null
        }
        label={`${rank}er`}
        size="small"
        sx={{
          height: '22px',
          fontWeight: 800,
          fontSize: '0.7rem',
          backgroundColor: badgeBg,
          color: badgeColor,
          border: '1px solid rgba(0,0,0,0.04)',
          mr: 1,
        }}
      />
    );
  };
  const getOccupancy = (totalParticipants, capacity) => {
    if (!capacity || capacity <= 0) {
      return null;
    }

    const percent = Math.min(
      100,
      Math.round((totalParticipants / capacity) * 100)
    );

    let statusLabel = '';
    if (percent === 0) statusLabel = 'Vide';
    else if (percent < 50) statusLabel = 'Faible remplissage';
    else if (percent < 80) statusLabel = 'Remplissage moyen';
    else if (percent < 100) statusLabel = 'Fort remplissage';
    else statusLabel = 'Complet';

    const isFull = totalParticipants >= capacity;

    return {
      percent,
      statusLabel,
      isFull,
    };
  };
  const handleExportExcel = () => {
    if (!data || data.length === 0) return;

    const rows = data.map((item) => {
      const title = item.title || item.analytics?.workshop?.title || '—';
      const formatRaw = item.format || item.analytics?.workshop?.format || '—';
      const formatLabel =
        formatRaw === 'en_presentiel'
          ? 'Présentiel'
          : formatRaw === 'zoom'
            ? 'Zoom'
            : 'En ligne';
      const intervenant = item.intervenant || item.analytics?.intervenant;

      const totalSessions =
        item.analytics?.workshop?.sessions_count ||
        (item.sessions?.length || 0) + (item.historique?.length || 0);

      const globalFillRate =
        item.analytics?.global_fill_rate || item.fill_rate || 0;
      const price = item.analytics?.workshop?.price || 0;
      const participantsCount = item.analytics?.global_participants || 0;
      const totalRevenue = item.revenue || price * participantsCount || 0;

      return {
        'ID Atelier': item.workshop_id || item.analytics?.workshop?.id || '—',
        "Titre de l'Atelier": title,
        Format: formatLabel,
        Intervenant: intervenant?.full_name || '—',
        'Spécialité Intervenant': intervenant?.main_speciality_user || '—',
        'Nombre total de Sessions': totalSessions,
        'Taux Remplissage Moyen (%)': `${globalFillRate}%`,
        'Total Participants': participantsCount,
        'Capacité Totale (Places)': item.analytics?.global_capacity || '—',
        "Chiffre d'Affaires (€)": totalRevenue,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Statistiques Globales');

    const maxProps = [
      { wch: 12 },
      { wch: 35 },
      { wch: 15 },
      { wch: 20 },
      { wch: 25 },
      { wch: 22 },
      { wch: 25 },
      { wch: 18 },
      { wch: 22 },
      { wch: 18 },
    ];
    worksheet['!cols'] = maxProps;

    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Statistiques_Ateliers_${dateStr}.xlsx`);
  };

  const isPopoverOpen = Boolean(anchorEl);

  return (
    <Box>
      {/* BARRE D'OUTILS */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2.5, flexWrap: 'wrap', gap: 2 }}
      >
        <ButtonGroup
          variant="outlined"
          sx={{
            bgcolor: '#f1f5f9',
            p: 0.5,
            borderRadius: '10px',
            border: 'none',
            '& .MuiButtonGroup-grouped': { border: 'none !important' },
          }}
        >
          <Button
            onClick={() => setViewMode('active')}
            startIcon={<SpeedIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 2,
              py: 0.75,
              color: viewMode === 'active' ? '#0f172a' : '#64748b',
              bgcolor: viewMode === 'active' ? '#ffffff' : 'transparent',
              boxShadow:
                viewMode === 'active' ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none',
            }}
          >
            Sessions actives
          </Button>
          <Button
            onClick={() => setViewMode('history')}
            startIcon={<HistoryIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 2,
              py: 0.75,
              color: viewMode === 'history' ? '#0f172a' : '#64748b',
              bgcolor: viewMode === 'history' ? '#ffffff' : 'transparent',
              boxShadow:
                viewMode === 'history'
                  ? '0 1px 3px 0 rgba(0,0,0,0.08)'
                  : 'none',
            }}
          >
            Historique
          </Button>
          <Button
            onClick={() => setViewMode('performance')}
            startIcon={<StarIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 2,
              py: 0.75,
              color: viewMode === 'performance' ? '#0f172a' : '#64748b',
              bgcolor: viewMode === 'performance' ? '#ffffff' : 'transparent',
              boxShadow:
                viewMode === 'performance'
                  ? '0 1px 3px 0 rgba(0,0,0,0.08)'
                  : 'none',
            }}
          >
            Top Performances
          </Button>
          <Button
            onClick={() => setViewMode('analytics')}
            startIcon={<BarChartIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 2,
              py: 0.75,
              color: viewMode === 'analytics' ? '#0f172a' : '#64748b',
              bgcolor: viewMode === 'analytics' ? '#ffffff' : 'transparent',
              boxShadow:
                viewMode === 'analytics'
                  ? '0 1px 3px 0 rgba(0,0,0,0.08)'
                  : 'none',
            }}
          >
            Analytique Globale
          </Button>
          <Button
            onClick={() => setViewMode('client')}
            startIcon={<PersonIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 2,
              py: 0.75,
              color: viewMode === 'client' ? '#0f172a' : '#64748b',
              bgcolor: viewMode === 'client' ? '#ffffff' : 'transparent',
              boxShadow:
                viewMode === 'client' ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none',
            }}
          >
            Meilleur client
          </Button>
        </ButtonGroup>

        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleExportExcel}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.85rem',
            bgcolor: '#10b981',
            borderRadius: '8px',
            px: 2.5,
            py: 0.9,
            boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
            '&:hover': { bgcolor: '#059669' },
          }}
        >
          Exporter Excel
        </Button>
      </Box>

      {/* TABLEAU */}
      <TableContainer
        sx={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
          bgcolor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        <Table sx={{ minWidth: 800 }}>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  py: 1.5,
                  width: '28%',
                }}
              >
                Atelier
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  py: 1.5,
                  width: '44%',
                }}
              >
                {viewMode === 'analytics'
                  ? 'Indicateurs Analytiques'
                  : viewMode === 'client'
                    ? 'Top Client Profil'
                    : 'Sessions & Remplissage'}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  py: 1.5,
                  width: '16%',
                }}
              >
                Intervenant
              </TableCell>

              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  py: 1.5,
                }}
              >
                Format
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  py: 1.5,
                }}
              >
                CA Global
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  py: 1.5,
                }}
              >
                Capacité & occupation
              </TableCell>

              <TableCell
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  py: 1.5,
                }}
              >
                Total Sessions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedWorkshops.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ py: 4, color: '#94a3b8', fontStyle: 'italic' }}
                >
                  Aucun atelier ne correspond aux critères actuels.
                </TableCell>
              </TableRow>
            ) : (
              displayedWorkshops.map((workshop, index) => {
                const title =
                  workshop.title ||
                  workshop.analytics?.workshop?.title ||
                  'Atelier Sans Titre';
                const capacity =
                  workshop.capacity || workshop.analytics?.workshop?.capacity;
                const format =
                  workshop.format || workshop.analytics?.workshop?.format;
                const workshopId =
                  workshop.workshop_id ||
                  workshop.analytics?.workshop?.id ||
                  index;

                const targetSessions =
                  viewMode === 'active'
                    ? workshop.sessions
                    : viewMode === 'history'
                      ? workshop.historique
                      : [
                          ...(workshop.sessions || []),
                          ...(workshop.historique || []),
                        ];

                const intervenant =
                  workshop.intervenant || workshop.analytics?.intervenant;
                const price = workshop.analytics?.workshop?.price || 0;
                const totalParticipants =
                  workshop.analytics?.global_participants || 0;
                const calculatedRevenue =
                  workshop.revenue || price * totalParticipants || 0;
                const futureSessions = workshop.sessions?.length || 0;
                const pastSessions = workshop.historique?.length || 0;

                const sessionCount = futureSessions + pastSessions;
                return (
                  <TableRow
                    key={workshopId}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      verticalAlign: 'top',
                    }}
                  >
                    <TableCell sx={{ py: 2.5 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={0.5}
                        sx={{ flexWrap: 'wrap', gap: '4px', mb: 0.5 }}
                      >
                        {renderRankBadge(index)}
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: '#0f172a' }}
                        >
                          {title}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', display: 'block' }}
                      >
                        ID: #{workshopId}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2.5 }}>
                      <WorkshopSessionsList
                        sessions={targetSessions}
                        viewMode={viewMode}
                        onOpenParticipants={handleOpenParticipants}
                        analyticsData={workshop.analytics}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 2.5 }}>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Avatar
                          src={
                            intervenant?.photo
                              ? `${apiUrl}/storage/workshop/${intervenant.photo}`
                              : undefined
                          }
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.75rem',
                            bgcolor: '#6366f1',
                            color: '#ffffff',
                            fontWeight: 700,
                            border: '1px solid #e2e8f0',
                          }}
                        >
                          {intervenant?.full_name
                            ? intervenant.full_name[0]
                            : '?'}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: '#334155',
                              display: 'block',
                            }}
                          >
                            {intervenant?.full_name || 'Non assigné'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#94a3b8', fontSize: '0.65rem' }}
                          >
                            {intervenant?.main_speciality_user || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        label={
                          format === 'en_presentiel'
                            ? 'Présentiel'
                            : format === 'zoom'
                              ? 'Zoom'
                              : 'En ligne'
                        }
                        size="small"
                        sx={{
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          bgcolor:
                            format === 'en_presentiel' ? '#f0fdf4' : '#f8fafc',
                          color:
                            format === 'en_presentiel' ? '#166534' : '#475569',
                          border: `1px solid ${format === 'en_presentiel' ? '#bbf7d0' : '#cbd5e1'}`,
                        }}
                      />
                    </TableCell>

                    <TableCell align="right" sx={{ py: 2.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: '#0f172a' }}
                      >
                        {new Intl.NumberFormat('fr-FR', {
                          style: 'currency',
                          currency: 'EUR',
                          maximumFractionDigits: 0,
                        }).format(calculatedRevenue)}
                      </Typography>
                      {price > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#94a3b8', fontSize: '0.65rem' }}
                        >
                          {price} € / pl.
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 2.5 }}>
                      {(() => {
                        const occupancy = getOccupancy(
                          totalParticipants,
                          capacity
                        );

                        if (!occupancy) return null;

                        const { percent, statusLabel, isFull } = occupancy;

                        return (
                          <Chip
                            icon={
                              <PeopleIcon
                                sx={{ fontSize: '12px !important' }}
                              />
                            }
                            label={`${totalParticipants}/${capacity} • ${percent}% • ${statusLabel}`}
                            size="small"
                            sx={{
                              fontSize: '0.68rem',
                              fontWeight: 600,
                              bgcolor: isFull
                                ? '#fee2e2'
                                : percent >= 80
                                  ? '#fff7ed'
                                  : '#f0fdf4',
                              color: isFull
                                ? '#dc2626'
                                : percent >= 80
                                  ? '#c2410c'
                                  : '#166534',
                              border: `1px solid ${
                                isFull
                                  ? '#fecaca'
                                  : percent >= 80
                                    ? '#fed7aa'
                                    : '#bbf7d0'
                              }`,
                            }}
                          />
                        );
                      })()}
                    </TableCell>

                    <TableCell sx={{ py: 2.5 }}>
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={`${sessionCount} sessions`}
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: '#e0f2fe',
                            color: '#0369a1',
                            border: '1px solid #bae6fd',
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* COLLAPSE BOUTON POUR LA LISTE DES ATELIERS */}
      {hasMoreWorkshops && (
        <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setWorkshopsExpanded(!workshopsExpanded)}
            endIcon={
              workshopsExpanded ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )
            }
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              color: '#475569',
              borderColor: '#cbd5e1',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
            }}
          >
            {workshopsExpanded
              ? "Voir moins d'ateliers"
              : `Voir plus d'ateliers (+${remainingWorkshopsCount})`}
          </Button>
        </Box>
      )}

      {/* POPOVER PARTICIPANTS */}
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleCloseParticipants}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: 260,
            maxHeight: 300,
            borderRadius: '10px',
            boxShadow: '0 4px 12px 0 rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0',
          },
        }}
      >
        <Box
          sx={{ p: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: '#475569', pl: 0.5 }}
          >
            Liste des participants ({activeParticipants.length})
          </Typography>
        </Box>
        <List dense sx={{ py: 0.5 }}>
          {activeParticipants.map((p, idx) => (
            <ListItem key={p.client_id || idx} sx={{ py: 0.5 }}>
              <ListItemAvatar sx={{ minWidth: 32 }}>
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    bgcolor: '#cbd5e1',
                    fontSize: '0.65rem',
                  }}
                >
                  {p.prenom ? (
                    p.prenom[0]
                  ) : (
                    <PersonIcon sx={{ fontSize: 12 }} />
                  )}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  `${p.prenom ?? ''} ${p.nom ?? ''}`.trim() || 'Client Anonyme'
                }
                secondary={p.email_client || p.email || undefined}
                primaryTypographyProps={{
                  variant: 'caption',
                  fontWeight: 600,
                  color: '#334155',
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: { fontSize: '0.6rem', color: '#94a3b8' },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Popover>
    </Box>
  );
}

AteliersSessionsAnalyticsTable.propTypes = {
  data: PropTypes.array,
};
